import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Settings, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { getIntegrationsConfig } from '../services/apiService';

const GestionDealflow = () => {
  const [pynnUrl, setPynnUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [showLoginHelp, setShowLoginHelp] = useState(false);
  const [loginWindowOpen, setLoginWindowOpen] = useState(false);
  const iframeRef = useRef(null);
  const loginCheckTimeoutRef = useRef(null);
  const loginWindowRef = useRef(null);
  const checkLoginIntervalRef = useRef(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await getIntegrationsConfig();
      if (response.success && response.config && response.config.pynn_url) {
        setPynnUrl(response.config.pynn_url);
      } else {
        setError('No hay URL de Pynn configurada');
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (iframeRef.current && pynnUrl) {
      console.log('🔄 Recargando iframe...');
      setIframeLoading(true);
      setIframeError(false);
      
      // Intentar usar Storage Access API si está disponible
      // Esto ayuda a solicitar acceso a cookies de terceros después de interacción del usuario
      if (document.hasStorageAccess) {
        try {
          const hasAccess = await document.hasStorageAccess();
          if (!hasAccess && document.requestStorageAccess) {
            try {
              await document.requestStorageAccess();
              console.log('✅ Acceso a storage concedido');
            } catch (e) {
              console.warn('⚠️ No se pudo obtener acceso a storage:', e);
              // Continuar de todas formas
            }
          }
        } catch (e) {
          console.warn('⚠️ Storage Access API no disponible:', e);
          // Continuar de todas formas
        }
      }
      
      // Limpiar completamente el iframe antes de recargar para evitar problemas de conexión
      iframeRef.current.src = 'about:blank';
      
      // Esperar un momento antes de cargar la nueva URL
      setTimeout(() => {
        if (iframeRef.current && pynnUrl) {
          // Recargar con la URL original (sin parámetros adicionales que puedan causar problemas)
          iframeRef.current.src = pynnUrl;
        }
      }, 200);
    }
  }, [pynnUrl]);

  const handleIframeLoad = () => {
    console.log('✅ Iframe cargado correctamente');
    setIframeLoading(false);
    setIframeError(false);
    
    // Limpiar timeout anterior si existe
    if (loginCheckTimeoutRef.current) {
      clearTimeout(loginCheckTimeoutRef.current);
    }
    
    // Verificar después de un tiempo si el usuario está atascado en el login
    loginCheckTimeoutRef.current = setTimeout(() => {
      if (iframeRef.current) {
        try {
          // Intentar detectar si estamos en la página de login
          // Esto solo funciona si es same-origin, pero intentamos de todas formas
          const iframeWindow = iframeRef.current.contentWindow;
          if (iframeWindow) {
            try {
              const currentUrl = iframeWindow.location.href;
              // Si la URL contiene /auth/login o /login, probablemente está atascado
              if (currentUrl.includes('/auth/login') || currentUrl.includes('/login')) {
                console.warn('⚠️ Detectado que el iframe está en la página de login. Puede ser un problema de cookies de terceros.');
                // No mostrar automáticamente, solo en consola para debugging
              }
            } catch (e) {
              // Cross-origin, no podemos acceder a la URL directamente
              // Esto es normal y esperado
            }
          }
        } catch (e) {
          // Error esperado si es cross-origin
        }
      }
    }, 5000); // Esperar 5 segundos después de cargar
  };
  
  useEffect(() => {
    // Escuchar mensajes de postMessage desde la ventana de login
    const handleMessage = (event) => {
      // Verificar que el mensaje viene de Pynn (mismo origen o dominio confiable)
      if (event.origin.includes('pynn.ai') || event.data?.type === 'pynn-login-success') {
        console.log('✅ Login exitoso detectado desde ventana externa');
        // Recargar el iframe
        handleRefresh();
        // Cerrar la ventana de login si está abierta
        if (loginWindowRef.current && !loginWindowRef.current.closed) {
          loginWindowRef.current.close();
        }
        setLoginWindowOpen(false);
        if (checkLoginIntervalRef.current) {
          clearInterval(checkLoginIntervalRef.current);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Limpiar timeout y listeners al desmontar
    return () => {
      window.removeEventListener('message', handleMessage);
      if (loginCheckTimeoutRef.current) {
        clearTimeout(loginCheckTimeoutRef.current);
      }
      if (checkLoginIntervalRef.current) {
        clearInterval(checkLoginIntervalRef.current);
      }
      if (loginWindowRef.current && !loginWindowRef.current.closed) {
        loginWindowRef.current.close();
      }
    };
  }, [handleRefresh]);

  const handleIframeError = () => {
    console.error('❌ Error en el iframe');
    setIframeLoading(false);
    setIframeError(true);
  };

  const handleOpenLoginWindow = () => {
    if (!pynnUrl) return;
    
    // Abrir ventana de login
    const loginWindow = window.open(
      pynnUrl,
      'pynn-login',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (loginWindow) {
      loginWindowRef.current = loginWindow;
      setLoginWindowOpen(true);
      setShowLoginHelp(false);
      
      // Verificar periódicamente si la ventana se cerró
      checkLoginIntervalRef.current = setInterval(() => {
        if (loginWindow.closed) {
          // La ventana se cerró, esperar un momento y recargar el iframe
          setTimeout(() => {
            console.log('🔄 Ventana de login cerrada, recargando iframe...');
            handleRefresh();
            setLoginWindowOpen(false);
          }, 1000);
          
          if (checkLoginIntervalRef.current) {
            clearInterval(checkLoginIntervalRef.current);
          }
        }
      }, 500);
    } else {
      alert('No se pudo abrir la ventana. Por favor, permite ventanas emergentes para este sitio.');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gestión Dealflow</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona tu dealflow desde Pynn
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !pynnUrl) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Gestión Dealflow</h1>
            <p className="text-muted-foreground mt-2">
              Gestiona tu dealflow desde Pynn
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/#/configuracion/integraciones'}
          >
            <Settings className="h-4 w-4 mr-2" />
            Configurar Pynn
          </Button>
        </div>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Pynn no configurado</h3>
            <p className="text-muted-foreground mb-4">
              {error || 'Para usar Gestión Dealflow, necesitas configurar la URL de Pynn en Configuración → Integraciones.'}
            </p>
            <Button onClick={() => window.location.href = '/#/configuracion/integraciones'}>
              <Settings className="h-4 w-4 mr-2" />
              Ir a Configuración
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gestión Dealflow</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona tu dealflow desde Pynn
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/#/configuracion/integraciones'}
        >
          <Settings className="h-4 w-4 mr-2" />
          Configurar
        </Button>
      </div>

      <Card className="border-border/50 shadow-sm flex-1 flex flex-col min-h-0 relative">
        <CardContent className="p-0 flex-1 min-h-0 relative">
          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Cargando Pynn...</p>
              </div>
            </div>
          )}
          {iframeError && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-4 text-destructive" />
                <p className="text-muted-foreground mb-4">Error al cargar Pynn</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reintentar
                </Button>
              </div>
            </div>
          )}
          {showLoginHelp && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/90 z-20 p-6">
              <Card className="max-w-md">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
                  <h3 className="text-lg font-semibold mb-2">Problema de Autenticación</h3>
                  <p className="text-muted-foreground mb-4">
                    Si no puedes hacer login dentro del iframe, es probable que sea un problema de cookies de terceros.
                    Haz clic en el botón de abajo para abrir Pynn en una nueva ventana, haz login allí, y luego haz clic en "Ya hice login". Después, deberás hacer login directamente en el iframe (las cookies no se comparten automáticamente entre ventanas e iframes).
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={handleOpenLoginWindow} variant="default">
                      Abrir Pynn en Nueva Ventana
                    </Button>
                    <Button onClick={() => setShowLoginHelp(false)} variant="outline">
                      Cerrar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {loginWindowOpen && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
              <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-yellow-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Ventana de login abierta</p>
                      <p className="text-xs text-muted-foreground">
                        Haz login en la nueva ventana. Cuando termines, haz clic en "Ya hice login" y luego haz login directamente en el iframe (las cookies no se comparten automáticamente).
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={async () => {
                          // Cerrar la ventana de login si está abierta
                          if (loginWindowRef.current && !loginWindowRef.current.closed) {
                            loginWindowRef.current.close();
                          }
                          setLoginWindowOpen(false);
                          if (checkLoginIntervalRef.current) {
                            clearInterval(checkLoginIntervalRef.current);
                          }
                          
                          // Intentar solicitar acceso a storage desde el iframe usando postMessage
                          // Esto requiere que el iframe coopere, pero intentamos de todas formas
                          if (iframeRef.current && iframeRef.current.contentWindow) {
                            try {
                              iframeRef.current.contentWindow.postMessage(
                                { type: 'request-storage-access' },
                                '*'
                              );
                            } catch (e) {
                              console.warn('No se pudo enviar mensaje al iframe:', e);
                            }
                          }
                          
                          // Esperar un momento para que las cookies se establezcan
                          await new Promise(resolve => setTimeout(resolve, 1000));
                          
                          // Recargar el iframe completamente (sin parámetros adicionales que puedan causar problemas)
                          if (iframeRef.current && pynnUrl) {
                            // Limpiar completamente el src y luego establecerlo de nuevo
                            // Esto fuerza una recarga completa del iframe
                            iframeRef.current.src = 'about:blank';
                            setTimeout(() => {
                              if (iframeRef.current) {
                                iframeRef.current.src = pynnUrl;
                                setIframeLoading(true);
                              }
                            }, 100);
                          }
                        }}
                      >
                        Ya hice login
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (loginWindowRef.current && !loginWindowRef.current.closed) {
                            loginWindowRef.current.close();
                          }
                          setLoginWindowOpen(false);
                          if (checkLoginIntervalRef.current) {
                            clearInterval(checkLoginIntervalRef.current);
                          }
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={pynnUrl}
            className="w-full h-full border-0"
            title="Pynn Dealflow"
            allow="fullscreen; autoplay; camera; microphone; geolocation; payment; clipboard-read; clipboard-write; storage-access"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation allow-modals allow-storage-access-by-user-activation allow-presentation allow-downloads"
            referrerPolicy="no-referrer-when-downgrade"
            loading="lazy"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ 
              minHeight: '600px',
              display: iframeError ? 'none' : 'block'
            }}
            // Agregar atributos adicionales para mejorar la estabilidad
            allowFullScreen
            frameBorder="0"
          />
          {!iframeLoading && !iframeError && (
            <div className="absolute bottom-4 right-4 z-10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLoginHelp(true)}
                title="¿Problemas con el login?"
              >
                <AlertCircle className="h-4 w-4 mr-2" />
                Ayuda con Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionDealflow;
